import { ActionPlanState } from "../components/LhActionPlanToggleSwitch";

export interface ActionPlanItem {
  id?: number;
  Id?: number; // API sometimes uses uppercase Id
  ActionPlanStatus?: string | number; // Can be string status or numeric Is_Active_Item
  Status?: string;
  SiteEvaluatorName?: string;
  Period?: string;
  Week?: string;
  Is_Active_Item?: number; // API field: 0, 1, or 2
  // Add other relevant fields as needed
}

/**
 * Determines the action plan state for a given item based on its Is_Active_Item value or ActionPlanStatus
 * @param item - The site evaluation item to check
 * @param activeItemsData - Array of active items data (for context, but not used for site eval metrics)
 * @returns ActionPlanState
 */
export const getActionPlanState = (
  item: ActionPlanItem,
  activeItemsData: ActionPlanItem[] = []
): ActionPlanState => {
  // For site evaluation metrics, use Is_Active_Item value directly
  if (typeof item.Is_Active_Item === "number") {
    switch (item.Is_Active_Item) {
      case 0:
        return "no-action-plan"; // Gray OFF
      case 1:
        return "pending-completion"; // Orange OFF  
      case 2:
        return "completed"; // Green ON
      default:
        return "no-action-plan";
    }
  }

  // For active items table, use ActionPlanStatus string
  if (item.ActionPlanStatus) {
    if (item.ActionPlanStatus === "Completed") {
      return "completed";
    } else if (item.ActionPlanStatus === "In Progress") {
      return "pending-completion";
    }
  }

  // For legacy support or when using ActionPlanStatus as number
  if (typeof item.ActionPlanStatus === "number") {
    switch (item.ActionPlanStatus) {
      case 0:
        return "no-action-plan";
      case 1:
        return "pending-completion";
      case 2:
        return "completed";
      default:
        return "no-action-plan";
    }
  }

  // Default fallback
  return "no-action-plan";
};

/**
 * Checks if an item should allow toggle interaction
 * @param item - The item to check
 * @param activeItemsData - Array of active items data
 * @param isActiveItemsTable - Whether this is being used in the active items table
 * @returns boolean
 */
export const shouldAllowToggleInteraction = (
  item: ActionPlanItem,
  activeItemsData: ActionPlanItem[] = [],
  isActiveItemsTable: boolean = false
): boolean => {
  const state = getActionPlanState(item, activeItemsData);
  
  // In active items table, allow interaction for pending items
  if (isActiveItemsTable) {
    return state === "pending-completion";
  }
  
  // In site eval metrics table, no interaction allowed (read-only)
  return false;
};

/**
 * Gets the display text for the action plan state
 * @param state - The action plan state
 * @returns string
 */
export const getActionPlanStateText = (state: ActionPlanState): string => {
  switch (state) {
    case "no-action-plan":
      return "No Action Plan Required";
    case "pending-completion":
      return "Action Plan Pending";
    case "completed":
      return "Action Plan Completed";
    default:
      return "Unknown Status";
  }
};
